/**
 * Hook to build amenities list from keywords_cz and listing data
 * 
 * This replaces hardcoded amenity lists with dynamic data from the database.
 * Icon names come from keywords_cz database, components are mapped in feature-icons.tsx.
 */

'use client'

import { useMemo } from 'react'
import type { Listing } from './types'
import { useKeywordsStore, useUIStore } from './store'
import { getIconComponent, getFeatureIcon } from './feature-icons'
import type { LucideIcon } from 'lucide-react'
import { localizeFeatureLabel } from './feature-labels'

export interface AmenityItem {
  key: string
  value: boolean
  label: string
  icon: LucideIcon
}

/**
 * Build amenities list from listing data and keywords_cz
 * 
 * @param listing - The listing object with has_* and is_* fields (can be null)
 * @returns Array of amenities that are present (value = true)
 */
export function useListingAmenities(listing: Listing | null): AmenityItem[] {
  const { keywords } = useKeywordsStore()
  const uiLanguage = useUIStore((state) => state.uiLanguage)
  
  return useMemo(() => {
    if (!listing || !keywords?.categories) {
      // Fallback: no listing or no keywords loaded yet
      return []
    }
    
    // Get all keyword definitions from keywords_cz
    const allKeywordDefs = Object.values(keywords.categories).flatMap(cat => cat.keywords)
    const keywordDefMap = new Map(allKeywordDefs.map(kw => [kw.feature_key, kw]))
    
    const amenities: AmenityItem[] = []
    
    // Simplified: Just check if feature_key is in listing.keywords array!
    // This replaces 50+ lines of complex pattern matching with a simple array check
    for (const featureKey of listing.keywords || []) {
      const keywordDef = keywordDefMap.get(featureKey)
      if (keywordDef) {
        amenities.push({
          key: featureKey,
          value: true,
          label: localizeFeatureLabel(featureKey, uiLanguage, keywordDef),
          icon: keywordDef.icon 
            ? getIconComponent(keywordDef.icon) // Icon name from database → component
            : getFeatureIcon(featureKey), // Fallback to hardcoded mapping
        })
      }
    }
    
    return amenities
  }, [listing, keywords, uiLanguage])
}

